/*
 * Client copy of the Agent tool input schemas. The server copy in
 * functions/lib/assistantTools.js is what the model sees; this one validates
 * tool inputs before an executor runs. tests/assistant-agent-registry.test.js
 * fails if the two drift apart.
 */

const GROUP = {
    type: "integer",
    description: "Event Group number as listed in the project context (1 = first group)."
};

const EVENT_NUMBERS = {
    type: "array",
    items: { type: "integer" },
    description: "Event numbers within the group (1 = first event)."
};

const SECONDS = (description) => ({ type: "number", description });

function schema(properties = {}, required = []) {
    return { type: "object", properties, required, additionalProperties: false };
}

/** @type {Record<string, object>} tool name -> input JSON schema */
export const TOOL_SCHEMAS = {
    get_project_summary: schema(),
    get_events: schema(
        {
            group: GROUP,
            from: { type: "integer", description: "First event number to return (inclusive)." },
            to: { type: "integer", description: "Last event number to return (inclusive)." },
            start_time: SECONDS("Only events that end after this time, in seconds from the start of the media."),
            end_time: SECONDS("Only events that start before this time, in seconds from the start of the media.")
        },
        ["group"]
    ),
    search_events: schema(
        {
            group: GROUP,
            text: { type: "string", description: "Text to find in the event text." },
            regex: { type: "boolean", description: "Treat text as a JavaScript regular expression." },
            case_sensitive: { type: "boolean", description: "Match case. Default false." },
            speaker: { type: "string", description: "Speaker name to match (case-insensitive)." },
            tag: { type: "string", description: "Tag to match (case-insensitive)." },
            empty: { type: "boolean", description: "When true, only events with no text." }
        },
        ["group"]
    ),
    get_qc_issues: schema(
        {
            group: GROUP,
            checks: {
                type: "array",
                items: { type: "string", enum: ["reading_speed", "overlap", "duration", "line_length"] },
                description: "Checks to run. Default: all."
            }
        },
        ["group"]
    ),
    update_events: schema(
        {
            group: GROUP,
            updates: {
                type: "array",
                description: "One entry per event to change.",
                items: {
                    type: "object",
                    properties: {
                        event: { type: "integer", description: "Event number (1 = first event)." },
                        text: { type: "string" },
                        start: { type: "number" },
                        end: { type: "number" },
                        notes: { type: "string" },
                        approved: { type: "boolean" },
                        forced: { type: "boolean" },
                        tags: { type: "array", items: { type: "string" } }
                    },
                    required: ["event"],
                    additionalProperties: false
                }
            }
        },
        ["group", "updates"]
    ),
    create_events: schema(
        {
            group: GROUP,
            events: {
                type: "array",
                items: {
                    type: "object",
                    properties: {
                        start: { type: "number" },
                        end: { type: "number" },
                        text: { type: "string" }
                    },
                    required: ["start", "end", "text"],
                    additionalProperties: false
                }
            }
        },
        ["group", "events"]
    ),
    delete_events: schema({ group: GROUP, events: EVENT_NUMBERS }, ["group", "events"]),
    search_replace: schema(
        {
            group: GROUP,
            find: { type: "string" },
            replace: { type: "string" },
            regex: { type: "boolean", description: "Treat find as a JavaScript regular expression; replace may use $1." },
            case_sensitive: { type: "boolean" },
            whole_word: { type: "boolean" },
            events: EVENT_NUMBERS
        },
        ["group", "find", "replace"]
    ),
    shift_timing: schema(
        {
            group: GROUP,
            offset: SECONDS("Seconds to add to start and end (negative moves earlier)."),
            fix_overlaps: { type: "boolean" },
            extend_to_min_duration: { type: "boolean" },
            events: EVENT_NUMBERS
        },
        ["group"]
    ),
    create_group: schema(
        {
            name: { type: "string" },
            language: { type: "string", description: "BCP 47 code, e.g. fr-FR." },
            type: { type: "string", enum: ["subtitle", "translation", "transcription"], description: "Default: translation when from_group is given, otherwise subtitle." },
            from_group: { type: "integer", description: "Group to copy the event timings from. A translation group is linked to it and keeps its text as each event's original." },
            copy: { type: "string", enum: ["timing", "timing_and_text"], description: "With from_group: copy only timings (text left empty, the default) or timings and text." }
        },
        ["name", "language"]
    ),
    set_group_settings: schema(
        {
            group: GROUP,
            name: { type: "string" },
            type: { type: "string", enum: ["subtitle", "transcription", "translation", "audio description"] },
            language: { type: "string", description: "BCP 47 code, e.g. en-US." },
            rtl: { type: "boolean" },
            maxCps: { type: "number" },
            maxWpm: { type: "number" },
            maxChars: { type: "integer" },
            maxLines: { type: "integer" },
            minDuration: { type: "number" },
            maxDuration: { type: "number" },
            overlap: { type: "boolean", description: "Whether overlapping events are allowed." }
        },
        ["group"]
    ),
    auto_format: schema({ group: GROUP, events: EVENT_NUMBERS }, ["group"]),
    correct_reading_speed: schema(
        {
            group: GROUP,
            events: EVENT_NUMBERS,
            allow_merge: { type: "boolean", description: "Allow merging with a neighbour. Default false." }
        },
        ["group"]
    ),
    seek: schema({ time: SECONDS("Seconds from the start of the media.") }, ["time"]),
    select_events: schema({ group: GROUP, events: EVENT_NUMBERS }, ["group", "events"]),
    select_group: schema({ group: GROUP }, ["group"])
};
